import carecentreTokens from "../tokens";
import { carecentreTypeScale as type } from "../typeScale";

const { color, font } = carecentreTokens;

export const CC_KLOE_TONES = [
  "safe",
  "effective",
  "caring",
  "responsive",
  "well-led",
];

export const CC_BADGE_TONES = {
  published: {
    color: color.success,
    background: "rgba(5, 150, 105, 0.12)",
  },
  completed: {
    color: color.success,
    background: "rgba(5, 150, 105, 0.12)",
  },
  success: {
    color: color.success,
    background: "rgba(5, 150, 105, 0.12)",
  },
  healthy: {
    color: color.success,
    background: "rgba(5, 150, 105, 0.12)",
  },
  responsive: {
    color: color.success,
    background: "rgba(5, 150, 105, 0.12)",
  },
  draft: {
    color: color.warning,
    background: "rgba(217, 119, 6, 0.12)",
  },
  pending: {
    color: color.warning,
    background: "rgba(217, 119, 6, 0.12)",
  },
  warning: {
    color: color.warning,
    background: "rgba(217, 119, 6, 0.12)",
  },
  watch: {
    color: color.warning,
    background: "rgba(217, 119, 6, 0.12)",
  },
  "well-led": {
    color: color.warning,
    background: "rgba(217, 119, 6, 0.12)",
  },
  archived: {
    color: color.danger,
    background: "rgba(220, 38, 38, 0.1)",
  },
  discontinued: {
    color: color.danger,
    background: "rgba(220, 38, 38, 0.1)",
  },
  danger: {
    color: color.danger,
    background: "rgba(220, 38, 38, 0.1)",
  },
  critical: {
    color: color.danger,
    background: "rgba(220, 38, 38, 0.1)",
  },
  safe: {
    color: color.danger,
    background: "rgba(220, 38, 38, 0.1)",
  },
  "in-progress": {
    color: color.info,
    background: "rgba(37, 99, 235, 0.1)",
  },
  info: {
    color: color.info,
    background: "rgba(37, 99, 235, 0.1)",
  },
  effective: {
    color: color.info,
    background: "rgba(37, 99, 235, 0.1)",
  },
  active: {
    color: color.primary,
    background: color.primarySoft,
  },
  inactive: {
    color: color.textMuted,
    background: color.bgBone,
  },
  ai: {
    color: "#EFEEE9",
    background: color.bgDark,
  },
  "ai-suggested": {
    color: "#EFEEE9",
    background: color.bgDark,
  },
  caring: {
    color: "#6D28D9",
    background: "rgba(109, 40, 217, 0.1)",
  },
  needs: {
    color: color.textInk,
    background: color.bgBone,
  },
  neutral: {
    color: color.textMuted,
    background: color.bgBone,
  },
};

export const normalizeBadgeTone = (tone = "neutral") =>
  String(tone || "neutral")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-");

const preserveAcronyms = (word) => {
  const upper = word.toUpperCase();
  if (["AI", "KLOE", "PRN", "NHS", "GP", "DNACPR"].includes(upper)) {
    return upper;
  }
  return `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`;
};

const isReactElement = (value) =>
  typeof value === "object" && value !== null && "$$typeof" in value;

export const formatBadgeLabel = (value, uppercase = false) => {
  if (value === null || value === undefined || isReactElement(value)) {
    return value;
  }

  const originalText = String(value).trim();
  if (!originalText) return originalText;
  if (!/^[a-zA-Z0-9 _-]+$/.test(originalText)) {
    return uppercase ? originalText.toUpperCase() : originalText;
  }

  const normalized = normalizeBadgeTone(originalText);
  const specialLabels = {
    "ai-suggested": "AI suggested",
    "in-progress": "In progress",
    "well-led": "Well-led",
  };

  if (!uppercase && specialLabels[normalized]) {
    return specialLabels[normalized];
  }

  const text = originalText.replace(/[_-]+/g, " ").trim();
  if (!text) return text;
  if (uppercase) return text.toUpperCase();

  return text
    .split(/\s+/)
    .map((word, index) => {
      if (word.toUpperCase() === "AI") return "AI";
      if (index === 0) return preserveAcronyms(word);
      const upper = word.toUpperCase();
      if (["KLOE", "PRN", "NHS", "GP", "DNACPR"].includes(upper)) {
        return upper;
      }
      return word.toLowerCase();
    })
    .join(" ");
};

export const getBadgeToneStyle = (tone) =>
  CC_BADGE_TONES[normalizeBadgeTone(tone)] || CC_BADGE_TONES.neutral;

export const getBadgeShape = ({ shape, pill, tone }) => {
  if (shape) return shape;
  if (pill) return "pill";
  if (CC_KLOE_TONES.includes(normalizeBadgeTone(tone))) return "kloe";
  return "status";
};

export const getBadgeBaseStyle = ({ shape = "status", uppercase = false }) => {
  const base = {
    width: "max-content",
    maxWidth: "100%",
    minWidth: 0,
    height: 24,
    minHeight: 24,
    borderRadius: 7,
    border: "1px solid transparent",
    fontFamily: font.body,
    fontSize: type.caption.fontSize,
    fontWeight: 700,
    letterSpacing: 0,
    lineHeight: `${type.caption.lineHeight}px`,
    textTransform: "none",
    whiteSpace: "nowrap",
  };

  if (shape === "kloe" || shape === "pill") {
    return {
      ...base,
      borderRadius: 9999,
    };
  }

  if (shape === "label") {
    return {
      ...base,
      height: 22,
      minHeight: 22,
      borderRadius: 7,
      fontFamily: font.mono,
      fontSize: type.label.fontSize,
      letterSpacing: "0.12em",
      lineHeight: `${type.label.lineHeight}px`,
      textTransform: "uppercase",
    };
  }

  if (shape === "ai") {
    return {
      ...base,
      height: 22,
      minHeight: 22,
      borderRadius: 4,
      lineHeight: "22px",
    };
  }

  return {
    ...base,
    textTransform: uppercase ? "uppercase" : "none",
  };
};

export const getBadgeLabelStyle = ({ shape = "status" }) => {
  if (shape === "kloe" || shape === "pill") {
    return {
      paddingLeft: 12,
      paddingRight: 12,
    };
  }

  if (shape === "ai") {
    return {
      paddingLeft: 9,
      paddingRight: 9,
    };
  }

  return {
    paddingLeft: 10,
    paddingRight: 10,
  };
};
